import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { BooruSite } from "./booru-api"
import { ALL_FILTER_RATINGS, sanitizeActiveRatings, type FilterRating } from "./rating-filter"

export type HandyPreviewPlaybackMode = "instant" | "smooth"

interface SettingsState {
  // 'auto' resolves per active source at render time (see booru-browser.tsx's
  // resolveLoadingMode) — most boorus stream real infinite feeds, but a few
  // (xlecx) only have numbered pages, so forcing one mode globally always
  // made one class of source or the other feel broken.
  loadingMode: 'infinite-scroll' | 'pagination' | 'auto'
  infiniteScroll: boolean // kept for backwards compat, derived from loadingMode
  infinitePagesMode: boolean // kept for backwards compat, derived from loadingMode
  pullToLoadEnabled: boolean
  pullDistanceThreshold: number // Added pull distance threshold for customizable swipe sensitivity
  postsPerPage: number
  imageQuality: "preview" | "sample" | "full" // Detail modal quality
  gridThumbnailQuality: "preview" | "sample" | "full" // Grid thumbnail quality
  
  // Universal credentials for boorus
  universalUsername: string
  universalPassword: string
  universalApiKey: string
  
  // Active sources selection (persisted across app restarts)
  selectedSites: BooruSite[]

  // Which ratings are switched on in the Rating filter (all three = nothing hidden). Persisted so a viewer who
  // chose safe-only stays safe-only after a reload.
  activeRatings: FilterRating[]
  
  autoplayVideos: boolean
  // How grid cards whose thumbnail is itself a video file behave: only load/play on hover,
  // load a looping preview whenever near the screen, or never load (plain tile).
  gridVideoPreviews: "hover" | "always" | "off"
  /** Force preview: the video post nearest the middle of the screen plays a muted looping preview in the grid, one at a time
   * (hooks/use-force-preview.ts). Off by default: it streams video the visitor did not ask for. */
  forcePreview: boolean
  setForcePreview: (value: boolean) => void
  /** Hovering a card that holds several pictures flips through them (hooks/use-album-hover-preview.ts). Off by default: Erome albums cost one extra request per hover. */
  albumHoverPreview: boolean
  setAlbumHoverPreview: (value: boolean) => void
  /** Next / previous tag gesture: swipe sideways near the bottom of the screen to step through the tag sets (components/tag-step-gesture.tsx). */
  tagStepGesture: boolean
  setTagStepGesture: (value: boolean) => void
  /** "continuous": keep stepping through every tag across the sets, wrapping at the end of the whole list. "set": wrap inside the
   * current set. (Older saved values "loop"/"nextGroup"/"random" are read as "continuous".) */
  tagStepEnd: "continuous" | "set"
  setTagStepEnd: (value: "continuous" | "set") => void
  /** In title search, also turn the typed phrase into real tags for the sites that only have tags (see lib/phrase-to-tags.ts) */
  phraseToTags: boolean
  setPhraseToTags: (value: boolean) => void
  setGridVideoPreviews: (mode: "hover" | "always" | "off") => void
  handMode: "right" | "left"
  // The real Cloudflare-block gesture direction is imageNavDirection/
  // infoSwipeDirection below — this was never wired into settings-panel.tsx
  // or read anywhere, removed 2026-08-18 along with the equally-orphaned
  // alwaysUseClientSide/autoVerifyOnFirstUse/showVerificationInEmptyState
  // (dead Cloudflare-bypass fields superseded by useCloudflareVerificationStore).
  imageNavDirection: "horizontal-next-right" | "horizontal-next-left" | "vertical-next-down" | "vertical-next-up"
  infoSwipeDirection: "left" | "right" | "up" | "down"
  gridLayout: "square" | "masonry" | "fitted"
  // Fitted layout only: every cell is the same shape; the fit decides how the image sits in it.
  gridFitShape: "landscape" | "portrait" | "square"
  gridFitMode: "contain" | "cover" | "fill"
  gridFitBlur: boolean // contain only: blurred copy of the image behind it instead of empty bars
  gridFlowDirection: "horizontal" | "vertical" // New setting for grid item flow
  gridGap: number
  gridColumns: number
  gridColumnsAuto: boolean // When true, column count is derived from available width instead of the fixed gridColumns value
  borderRadius: number
  showButtonLabels: boolean
  hideScrollbar: boolean
  shuffleMode: boolean
  smartShuffleMode: boolean

  showScrollButtons: boolean
  floatingButtonsAutoHide: boolean
  floatingButtonsAutoHideDelay: number // seconds: 3, 5, 10, 0 = never
  searchBarPosition: "top" | "bottom"
  stickyHeader: boolean
  performanceMode: "low" | "medium" | "high"
  cacheLimit: number
  prefetchAmount: number

  stealthMode: boolean
  saveSearchHistory: boolean
  saveViewHistory: boolean
  saveDownloadHistory: boolean
  clearClipboardAfter: number | null
  cacheImages: boolean
  clearTempOnExit: boolean
  passwordProtectedDownloads: boolean
  hideFromRecentFiles: boolean
  rotateUserAgents: boolean
  stripReferrerHeaders: boolean
  randomizeRequestTiming: boolean
  panicButtonEnabled: boolean
  panicAction: "close" | "fake-screen" | "clear-close"
  autoLockAfter: number | null
  blurThumbnails: boolean
  disableScreenshots: boolean
  appLocked: boolean
  appLockType: "pin" | "password" | "none"
  appLockCode: string
  panicButtonKeybind: string

  theme: "dark" | "light" | "auto"
  goonGlassesIntensity: number // added goon glasses intensity setting (0-100)
  booruModalLayout: "classic" | "fullscreen"
  eromeModalPositioning: "auto" | "centered" | "pinned"

  mouseWheelBehavior: "zoom" | "navigate" | "pan"
  enableMousePan: boolean
  panSensitivity: number

  filterAIImages: boolean
  tagsScrollableMode: boolean // added tagsScrollableMode to toggle between wrap and scroll

  saveLocation: "downloads" | "documents" | "custom"
  customSavePath: string
  createSubfolder: boolean
  autoDownloadWithoutDialog: boolean

  videoPitchCorrection: boolean
  videoDefaultSpeed: number
  videoAutoMute: boolean
  
  searchMode: "tags" | "title" // Search by tags or by title/phrase
  sortDataSource: "app" | "source" // Sort by app data (ratings, favorites) or source data (score, date)

  // Nested grid navigation settings
  nestedGridMode: boolean // Global nested grid mode toggle
  nestedGridThreshold: number // Album size threshold for automatic mode selection
  perSourceNestedMode: Record<string, boolean> // Per-source nested grid mode overrides

  // Editor preferences
  showPointGapTooltip: boolean
  handyPreviewPlaybackMode: HandyPreviewPlaybackMode
  cloudSyncEnabled: boolean // Auto-sync personal scripts to account

  setLoadingMode: (mode: 'infinite-scroll' | 'pagination' | 'auto') => void
  setInfiniteScroll: (enabled: boolean) => void
  setInfinitePagesMode: (enabled: boolean) => void // New setter
  setPullToLoadEnabled: (enabled: boolean) => void
  setPullDistanceThreshold: (pixels: number) => void // Added setter for pull distance threshold
  setPostsPerPage: (count: number) => void
  setImageQuality: (quality: "preview" | "sample" | "full") => void
  setGridThumbnailQuality: (quality: "preview" | "sample" | "full") => void
  
  setUniversalUsername: (username: string) => void
  setUniversalPassword: (password: string) => void
  setUniversalApiKey: (apiKey: string) => void
  
  setSelectedSites: (sites: BooruSite[]) => void
  setActiveRatings: (ratings: FilterRating[]) => void

  setAutoplayVideos: (enabled: boolean) => void
  setHandMode: (mode: "right" | "left") => void
  setImageNavDirection: (
    direction: "horizontal-next-right" | "horizontal-next-left" | "vertical-next-down" | "vertical-next-up",
  ) => void
  setInfoSwipeDirection: (direction: "left" | "right" | "up" | "down") => void
  setGridLayout: (layout: "square" | "masonry" | "fitted") => void
  setGridFitShape: (shape: "landscape" | "portrait" | "square") => void
  setGridFitMode: (mode: "contain" | "cover" | "fill") => void
  setGridFitBlur: (on: boolean) => void
  setGridFlowDirection: (direction: "horizontal" | "vertical") => void // New setter
  setGridGap: (gap: number) => void
  setGridColumns: (columns: number) => void
  setGridColumnsAuto: (auto: boolean) => void
  resetGridColumns: () => void // added reset grid columns function
  setBorderRadius: (radius: number) => void
  setShowButtonLabels: (show: boolean) => void
  setHideScrollbar: (hide: boolean) => void
  setShuffleMode: (enabled: boolean) => void
  setSmartShuffleMode: (enabled: boolean) => void

  setShowScrollButtons: (show: boolean) => void
  setFloatingButtonsAutoHide: (enabled: boolean) => void
  setFloatingButtonsAutoHideDelay: (seconds: number) => void
  setSearchBarPosition: (position: "top" | "bottom") => void
  setStickyHeader: (sticky: boolean) => void
  setPerformanceMode: (mode: "low" | "medium" | "high") => void
  setCacheLimit: (limit: number) => void
  setPrefetchAmount: (amount: number) => void

  setStealthMode: (enabled: boolean) => void
  setSaveSearchHistory: (save: boolean) => void
  setSaveViewHistory: (save: boolean) => void
  setSaveDownloadHistory: (save: boolean) => void
  setClearClipboardAfter: (seconds: number | null) => void
  setCacheImages: (cache: boolean) => void
  setClearTempOnExit: (clear: boolean) => void
  setPasswordProtectedDownloads: (enabled: boolean) => void
  setHideFromRecentFiles: (hide: boolean) => void
  setRotateUserAgents: (enabled: boolean) => void
  setStripReferrerHeaders: (strip: boolean) => void
  setRandomizeRequestTiming: (enabled: boolean) => void
  setPanicButtonEnabled: (enabled: boolean) => void
  setPanicAction: (action: "close" | "fake-screen" | "clear-close") => void
  setAutoLockAfter: (minutes: number | null) => void
  setBlurThumbnails: (blur: boolean) => void
  setDisableScreenshots: (disable: boolean) => void
  setAppLocked: (locked: boolean) => void
  setAppLockType: (type: "pin" | "password" | "none") => void
  setAppLockCode: (code: string) => void
  setPanicButtonKeybind: (keybind: string) => void

  setTheme: (theme: "dark" | "light" | "auto") => void
  setGoonGlassesIntensity: (intensity: number) => void // added setter for goon glasses
  setBooruModalLayout: (layout: "classic" | "fullscreen") => void
  setEromeModalPositioning: (positioning: "auto" | "centered" | "pinned") => void

  setMouseWheelBehavior: (behavior: "zoom" | "navigate" | "pan") => void
  setEnableMousePan: (enabled: boolean) => void
  setPanSensitivity: (sensitivity: number) => void

  setFilterAIImages: (filter: boolean) => void // added AI filter setter
  setTagsScrollableMode: (enabled: boolean) => void // added setter for scrollable mode

  // Added setters for save location
  setSaveLocation: (location: "downloads" | "documents" | "custom") => void
  setCustomSavePath: (path: string) => void
  setCreateSubfolder: (create: boolean) => void
  setAutoDownloadWithoutDialog: (auto: boolean) => void

  setVideoPitchCorrection: (enabled: boolean) => void
  setVideoDefaultSpeed: (speed: number) => void
  setVideoAutoMute: (mute: boolean) => void
  
  setSearchMode: (mode: "tags" | "title") => void
  setSortDataSource: (source: "app" | "source") => void
  
  setNestedGridMode: (enabled: boolean) => void
  setNestedGridThreshold: (threshold: number) => void
  // enabled: undefined clears the override (falls back to source default / global setting)
  setPerSourceNestedMode: (source: string, enabled: boolean | undefined) => void
  setShowPointGapTooltip: (enabled: boolean) => void
  setHandyPreviewPlaybackMode: (mode: HandyPreviewPlaybackMode) => void
  setCloudSyncEnabled: (enabled: boolean) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      loadingMode: 'auto' as const,
      infiniteScroll: true,
      infinitePagesMode: false, // New setting - disabled by default
      pullToLoadEnabled: true,
      pullDistanceThreshold: 60, // Default pull threshold of 60px (user can adjust 20-200px)
      postsPerPage: 25,
      imageQuality: "sample",
      gridThumbnailQuality: "preview", // Default to preview for faster grid loading
      
      universalUsername: "",
      universalPassword: "",
      universalApiKey: "",
      
      selectedSites: [], // Start with no sites selected - user must choose
      activeRatings: ["safe", "questionable"], // default stops at questionable; explicit is opt-in (may change with tester feedback)

      autoplayVideos: false,
      gridVideoPreviews: "hover",
      forcePreview: false,
      albumHoverPreview: false,
      tagStepGesture: true,
      tagStepEnd: "continuous",
      phraseToTags: false,
      handMode: "right",
      imageNavDirection: "horizontal-next-right",
      infoSwipeDirection: "left",
      gridLayout: "masonry", // Back to masonry as default
      // Raw stored value "vertical" is what the Settings UI displays as
      // "Horizontal" (see settings-panel.tsx's intentionally-inverted
      // SelectItem values — booru-grid.tsx's reorder condition for masonry
      // is `gridFlowDirection === "vertical"`). Masonry's CSS column-count
      // layout natively fills column-by-column (vertical-first), so this
      // needs to be "vertical" to actually trigger the row-major transpose
      // by default — the previous default ("horizontal" raw) left masonry's
      // native vertical fill unreordered, which is why items appeared to
      // repeat/lose their search-relevance order as more pages loaded in.
      gridFitShape: "square",
      gridFitMode: "contain",
      gridFitBlur: true,
      gridFlowDirection: "vertical", // Displays as "Horizontal" in Settings — see comment above
      gridGap: 3,
      gridColumns: 3,
      gridColumnsAuto: false,
      borderRadius: 8,
      showButtonLabels: false,
      hideScrollbar: true,
      shuffleMode: false,
      smartShuffleMode: false,

      showScrollButtons: true,
      floatingButtonsAutoHide: true,
      floatingButtonsAutoHideDelay: 5,
      searchBarPosition: "top",
      stickyHeader: true,
      performanceMode: "medium",
      cacheLimit: 100,
      prefetchAmount: 5,

      stealthMode: false,
      saveSearchHistory: true,
      saveViewHistory: true,
      saveDownloadHistory: true,
      clearClipboardAfter: null,
      cacheImages: true,
      clearTempOnExit: false,
      passwordProtectedDownloads: false,
      hideFromRecentFiles: false,
      rotateUserAgents: false,
      stripReferrerHeaders: false,
      randomizeRequestTiming: false,
      panicButtonEnabled: false,
      panicAction: "close",
      autoLockAfter: null,
      blurThumbnails: false,
      disableScreenshots: false,
      appLocked: false,
      appLockType: "none",
      appLockCode: "",
      panicButtonKeybind: "Ctrl+Shift+Q",

      theme: "light",
      goonGlassesIntensity: 0, // default to no filter
      booruModalLayout: "classic",
      eromeModalPositioning: "auto",

      mouseWheelBehavior: "zoom",
      enableMousePan: true,
      panSensitivity: 1,

      filterAIImages: false, // default to showing AI images
      tagsScrollableMode: true, // default to scroll mode; user can switch to wrap in Settings

      saveLocation: "downloads",
      customSavePath: "",
      createSubfolder: false,
      autoDownloadWithoutDialog: false,

      videoPitchCorrection: true,
      videoDefaultSpeed: 1,
      videoAutoMute: false,
      
      searchMode: "tags",
      sortDataSource: "source", // Default to source data (traditional behavior)

      // Nested grid navigation settings defaults
      nestedGridMode: false, // Default to modal mode
      nestedGridThreshold: 20, // Albums with 20+ images use nested mode
      perSourceNestedMode: {}, // No per-source overrides by default

      showPointGapTooltip: true,
      handyPreviewPlaybackMode: "instant",
      cloudSyncEnabled: true,

      setLoadingMode: (mode) => set({
        loadingMode: mode,
        // 'auto' resolves per-source at render time in booru-browser.tsx —
        // this flag is only the coarse global fallback for code that hasn't
        // been updated to read the resolved per-source mode.
        infiniteScroll: mode !== 'pagination',
        infinitePagesMode: false, // always off now
      }),
      setInfiniteScroll: (enabled) => set({ infiniteScroll: enabled }),
      setInfinitePagesMode: (enabled) => set({ infinitePagesMode: enabled }),
      setPullToLoadEnabled: (enabled) => set({ pullToLoadEnabled: enabled }),
      setPullDistanceThreshold: (pixels) => set({ pullDistanceThreshold: Math.max(20, Math.min(200, pixels)) }), // Setter implementation for pull distance threshold
      setPostsPerPage: (count) => set({ postsPerPage: Math.max(1, Math.min(100, count)) }),
      setImageQuality: (quality) => set({ imageQuality: quality }),
      setGridThumbnailQuality: (quality) => set({ gridThumbnailQuality: quality }),
      
      setUniversalUsername: (username) => set({ universalUsername: username }),
      setUniversalPassword: (password) => set({ universalPassword: password }),
      setUniversalApiKey: (apiKey) => set({ universalApiKey: apiKey }),
      
      setSelectedSites: (sites) => set({ selectedSites: sites }),
      setActiveRatings: (ratings) => set({ activeRatings: sanitizeActiveRatings(ratings) }),

      setAutoplayVideos: (enabled) => set({ autoplayVideos: enabled }),
      setGridVideoPreviews: (mode) => set({ gridVideoPreviews: mode }),
      setForcePreview: (value) => set({ forcePreview: value }),
      setAlbumHoverPreview: (value) => set({ albumHoverPreview: value }),
      setTagStepGesture: (value) => set({ tagStepGesture: value }),
      setTagStepEnd: (value) => set({ tagStepEnd: value }),
      setPhraseToTags: (value) => set({ phraseToTags: value }),
      setHandMode: (mode) => set({ handMode: mode }),
      setImageNavDirection: (direction) => set({ imageNavDirection: direction }),
      setInfoSwipeDirection: (direction) => set({ infoSwipeDirection: direction }),
      setGridLayout: (layout) => set({ gridLayout: layout }),
      setGridFitShape: (shape) => set({ gridFitShape: shape }),
      setGridFitMode: (mode) => set({ gridFitMode: mode }),
      setGridFitBlur: (on) => set({ gridFitBlur: on }),
      setGridFlowDirection: (direction) => set({ gridFlowDirection: direction }), // New setter implementation
      setGridGap: (gap) => set({ gridGap: Math.max(0, Math.min(10, gap)) }),
      setGridColumns: (columns) => set({ gridColumns: Math.max(1, Math.min(10, columns)) }), // Back to min 1
      setGridColumnsAuto: (auto) => set({ gridColumnsAuto: auto }),
      resetGridColumns: () => {
        set({ gridColumns: 3 })
        localStorage.removeItem("responsive-grid-set")
        console.log("[v0] Grid columns reset. Refresh page to test responsive defaults (5 desktop/3 tablet/2 mobile)")
      },
      setBorderRadius: (radius) => set({ borderRadius: Math.max(0, Math.min(50, radius)) }),
      setShowButtonLabels: (show) => set({ showButtonLabels: show }),
      setHideScrollbar: (hide) => set({ hideScrollbar: hide }),
      setShuffleMode: (enabled) => set({ shuffleMode: enabled }),
      setSmartShuffleMode: (enabled) => set({ smartShuffleMode: enabled }),

      setShowScrollButtons: (show) => set({ showScrollButtons: show }),
      setFloatingButtonsAutoHide: (enabled) => set({ floatingButtonsAutoHide: enabled }),
      setFloatingButtonsAutoHideDelay: (seconds) => set({ floatingButtonsAutoHideDelay: seconds }),
      setSearchBarPosition: (position) => set({ searchBarPosition: position }),
      setStickyHeader: (sticky) => set({ stickyHeader: sticky }),
      setPerformanceMode: (mode) => set({ performanceMode: mode }),
      setCacheLimit: (limit) => set({ cacheLimit: Math.max(0, Math.min(1000, limit)) }),
      setPrefetchAmount: (amount) => set({ prefetchAmount: Math.max(0, Math.min(50, amount)) }),

      setStealthMode: (enabled) => set({ stealthMode: enabled }),
      setSaveSearchHistory: (save) => set({ saveSearchHistory: save }),
      setSaveViewHistory: (save) => set({ saveViewHistory: save }),
      setSaveDownloadHistory: (save) => set({ saveDownloadHistory: save }),
      setClearClipboardAfter: (seconds) => set({ clearClipboardAfter: seconds }),
      setCacheImages: (cache) => set({ cacheImages: cache }),
      setClearTempOnExit: (clear) => set({ clearTempOnExit: clear }),
      setPasswordProtectedDownloads: (enabled) => set({ passwordProtectedDownloads: enabled }),
      setHideFromRecentFiles: (hide) => set({ hideFromRecentFiles: hide }),
      setRotateUserAgents: (enabled) => set({ rotateUserAgents: enabled }),
      setStripReferrerHeaders: (strip) => set({ stripReferrerHeaders: strip }),
      setRandomizeRequestTiming: (enabled) => set({ randomizeRequestTiming: enabled }),
      setPanicButtonEnabled: (enabled) => set({ panicButtonEnabled: enabled }),
      setPanicAction: (action) => set({ panicAction: action }),
      setAutoLockAfter: (minutes) => set({ autoLockAfter: minutes }),
      setBlurThumbnails: (blur) => set({ blurThumbnails: blur }),
      setDisableScreenshots: (disable) => set({ disableScreenshots: disable }),
      setAppLocked: (locked) => set({ appLocked: locked }),
      setAppLockType: (type) => set({ appLockType: type }),
      setAppLockCode: (code) => set({ appLockCode: code }),
      setPanicButtonKeybind: (keybind) => set({ panicButtonKeybind: keybind }),

      setTheme: (theme) => set({ theme }),
      setGoonGlassesIntensity: (intensity) => set({ goonGlassesIntensity: Math.max(0, Math.min(100, intensity)) }), // setter implementation

      setBooruModalLayout: (layout) => set({ booruModalLayout: layout }),
      setEromeModalPositioning: (positioning) => set({ eromeModalPositioning: positioning }),
      setMouseWheelBehavior: (behavior) => set({ mouseWheelBehavior: behavior }),
      setEnableMousePan: (enabled) => set({ enableMousePan: enabled }),
      setPanSensitivity: (sensitivity) => set({ panSensitivity: Math.max(0.1, Math.min(5, sensitivity)) }),


      setFilterAIImages: (filter) => set({ filterAIImages: filter }), // added AI filter setter
      setTagsScrollableMode: (enabled) => set({ tagsScrollableMode: enabled }), // added setter implementation

      setSaveLocation: (location) => set({ saveLocation: location }),
      setCustomSavePath: (path) => set({ customSavePath: path }),
      setCreateSubfolder: (create) => set({ createSubfolder: create }),
      setAutoDownloadWithoutDialog: (auto) => set({ autoDownloadWithoutDialog: auto }),

      setVideoPitchCorrection: (enabled) => set({ videoPitchCorrection: enabled }),
      setVideoDefaultSpeed: (speed) => set({ videoDefaultSpeed: Math.max(0.25, Math.min(3, speed)) }),
      setVideoAutoMute: (mute) => set({ videoAutoMute: mute }),
      
      setSearchMode: (mode) => set({ searchMode: mode }),
      setSortDataSource: (source) => set({ sortDataSource: source }),
      
      setNestedGridMode: (enabled) => set({ nestedGridMode: enabled }),
      setNestedGridThreshold: (threshold) => set({ nestedGridThreshold: Math.max(0, threshold) }),
      setShowPointGapTooltip: (enabled) => set({ showPointGapTooltip: enabled }),
      setHandyPreviewPlaybackMode: (mode) => set({ handyPreviewPlaybackMode: mode }),
      setCloudSyncEnabled: (enabled) => set({ cloudSyncEnabled: enabled }),
      setPerSourceNestedMode: (source, enabled) =>
        set((state) => {
          const next = { ...state.perSourceNestedMode }
          if (enabled === undefined) {
            delete next[source]
          } else {
            next[source] = enabled
          }
          return { perSourceNestedMode: next }
        }),
    }),
    {
      name: "booru-settings",
      version: 2,
      migrate: (persistedState, version) => {
        const state = persistedState as SettingsState
        // Pre-v1 stores predate rounded corners becoming the default; only
        // migrate untouched-zero values, not an explicit user choice of 0.
        if (version < 1 && state?.borderRadius === 0) {
          state.borderRadius = 4
        }
        // v1 stores landed on borderRadius 4; bump untouched 4s to the new 8 default.
        if (version < 2 && state?.borderRadius === 4) {
          state.borderRadius = 8
        }
        return state
      },
    },
  ),
)

if (typeof window !== "undefined") {
  ;(window as any).resetToFirstTimeVisitor = () => {
    const store = useSettingsStore.getState()
    store.resetGridColumns()
    console.log("[v0] Reset complete! Refresh the page to test responsive defaults.")
  }
}
